// src/components/LayerPanel/useActiveLayers.js
//
// Single source of truth for "what's active" in the layer panel.
//
// "Active" = a layer the user has switched on — part of their working set and
// contributing (or intended to contribute) to the map render. This definition
// is applied identically across every scope:
//   - imagery: an entry exists in the imagery stack (incl. degraded entries
//     that have no acquisition at the current cursor — still "switched on");
//   - features: a static layer is visible, or an asset geometry is enabled;
//   - product: a product-context layer is visible;
//   - user layers: the layer's registry fill entry is visible.
// AOI selection is not a layer and is deliberately not counted.
//
// The footer count and "Clear all" both derive from this hook, so the number
// shown to the user means exactly one thing.

import { useMemo, useCallback } from "react";
import { useLayersStore } from "../../stores/useLayersStore";
import { useLayerRegistryStore } from "../../stores/useLayerRegistryStore";
import { useProductLayerStore } from "../../stores/useProductLayerStore";
import { useImageryStackStore } from "../../stores/useImageryStackStore";
import { LAYER_GROUPS_STATIC } from "../../layers/layerPanelConfigStatic";

export default function useActiveLayers() {
  const registryLayers = useLayerRegistryStore((s) => s.layers);
  const visibleById = useLayersStore((s) => s.visibleById);
  const assets = useLayersStore((s) => s.assets);
  const productLayers = useProductLayerStore((s) => s.layers);
  const featureCatalog = useLayersStore((s) => s.featureCatalog);
  const activeFeatureTypes = useLayersStore((s) => s.activeFeatureTypes);

  const activeLayers = useMemo(() => {
    const results = [];
    const seen = new Set();

    // 1. Registry layers — imagery (incl. degraded, still "on") + user layers.
    Object.values(registryLayers).forEach((l) => {
      if (l.scope === "imagery") {
        if (!l.visible && !l.meta?.degraded) return;
      } else if (!l.visible) {
        return;
      }
      if (l.kind === "user-layer-outline") return;
      if (l.scope === "deleted") return;

      results.push({
        id: l.id,
        label: l.name ?? l.meta?.label ?? l.id,
        scope: l.scope,
        _source: "registry",
      });
      seen.add(l.id);
    });

    // 2. Static feature layers from the operational config tree.
    const walkGroups = (items, inheritedAsset = null) => {
      items.forEach((g) => {
        if (g.comingSoon) return;
        const effectiveAsset = g.asset ?? inheritedAsset;
        if (g.layers) {
          g.layers.forEach((l) => {
            if (l.comingSoon || seen.has(l.id)) return;
            const layerAsset = l.asset ?? effectiveAsset;
            const on = layerAsset
              ? assets?.[layerAsset]?.enabled && assets?.[layerAsset]?.geometry?.[l.geometry]
              : visibleById[l.id];
            if (!on) return;
            seen.add(l.id);
            results.push({
              id: l.id,
              label: l.label ?? l.id,
              scope: "project",
              assetType: l.assetType ?? null,
              _source: "static",
              _asset: layerAsset,
              _geometry: l.geometry,
            });
          });
        }
        if (g.groups) walkGroups(g.groups, effectiveAsset);
      });
    };
    walkGroups(LAYER_GROUPS_STATIC);

    // 3. Product-context layers.
    Object.values(productLayers).forEach((l) => {
      if (!l.visible || seen.has(l.id)) return;
      seen.add(l.id);
      results.push({
        id: l.id,
        label: l.label,
        scope: "product",
        _source: "product",
        _productKey: l.productKey,
      });
    });

    // 4. Derived vector_assets feature types (FEATURES-tab flat list). On-state
    //    is the generic active set, except well_pad which mirrors assets.pads
    //    (rendered by PadLayer) — same routing as useLayersStore.isFeatureTypeOn.
    (featureCatalog ?? []).forEach((f) => {
      const on =
        f.key === "well_pad"
          ? !!(assets?.pads?.enabled && assets?.pads?.geometry?.polygon)
          : !!activeFeatureTypes[f.key];
      if (!on) return;
      const id = `feature:${f.key}`;
      if (seen.has(id)) return;
      seen.add(id);
      results.push({
        id,
        label: f.label,
        scope: "feature",
        assetType: f.key,
        _source: "feature",
        _featureKey: f.key,
      });
    });

    return results;
  }, [registryLayers, visibleById, assets, productLayers, featureCatalog, activeFeatureTypes]);

  // Turn one layer off, routed by source so each owning store stays in sync.
  // Mirrors the per-source removal logic from the old Active Layers tray.
  const deactivateLayer = useCallback((layer) => {
    if (layer._source === "product") {
      useProductLayerStore.getState().setProductLayerVisible(layer.id, false);
      window.dispatchEvent(
        new CustomEvent("aiprime:hide-layer", { detail: { id: layer.id } })
      );
      return;
    }
    if (layer._source === "feature") {
      useLayersStore.getState().toggleFeatureType(layer._featureKey);
      return;
    }
    if (layer._source === "registry" && layer.scope === "imagery") {
      useImageryStackStore.getState().removeLayer(layer.id);
      return;
    }
    if (layer._source === "registry") {
      const registry = useLayerRegistryStore.getState();
      const rl = registry.layers[layer.id];
      if (rl) {
        registry.toggleLayer(layer.id);
        if (rl.scope === "user" && rl.db_layer_id) {
          const outlineId = `user-layer-${rl.db_layer_id}-line`;
          if (registry.layers[outlineId]?.visible) registry.toggleLayer(outlineId);
        }
      }
      return;
    }
    if (layer._asset && layer._geometry) {
      useLayersStore.getState().toggleAssetGeometry(layer._asset, layer._geometry);
      return;
    }
    useLayersStore.getState().toggleLayer(layer.id);
  }, []);

  const clearAll = useCallback(() => {
    activeLayers.forEach(deactivateLayer);
  }, [activeLayers, deactivateLayer]);

  return { activeLayers, count: activeLayers.length, deactivateLayer, clearAll };
}
